import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
	CreateSystemBranchBody,
	UpdateBranchCustomNameBody,
	UpdateBranchVisibilityBody,
	UpdateCROverridesBody,
} from '@/types/organization-overrides'
import {
	createSystemBranch,
	deleteSystemBranch,
	updateBranchCustomName,
	updateBranchVisibility,
	updateCROverrides,
} from '../services/organization-overrides'
import { organizationKeys } from './organization-queries'

// ── POST /organization/branches (create virtual branch) ───────────────────────

export function useCreateSystemBranch() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateSystemBranchBody) => createSystemBranch(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: organizationKeys.all })
			queryClient.invalidateQueries({ queryKey: organizationKeys.admin })
		},
	})
}

// ── PATCH /organization/branches/:id/customize ────────────────────────────────

export function useUpdateBranchCustomName() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string
			body: UpdateBranchCustomNameBody
		}) => updateBranchCustomName(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: organizationKeys.all })
			queryClient.invalidateQueries({ queryKey: organizationKeys.admin })
		},
	})
}

// ── PATCH /organization/crs/:id/customize ─────────────────────────────────────

export function useUpdateCROverrides() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpdateCROverridesBody }) =>
			updateCROverrides(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: organizationKeys.all })
			queryClient.invalidateQueries({ queryKey: organizationKeys.admin })
		},
	})
}

// ── PATCH /organization/branches/:id/visibility ───────────────────────────────

export function useUpdateBranchVisibility() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string
			body: UpdateBranchVisibilityBody
		}) => updateBranchVisibility(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: organizationKeys.all })
			queryClient.invalidateQueries({ queryKey: organizationKeys.admin })
		},
	})
}

// ── DELETE /organization/branches/:id ────────────────────────────────────────

export function useDeleteSystemBranch() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id }: { id: string }) => deleteSystemBranch(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: organizationKeys.all })
			queryClient.invalidateQueries({ queryKey: organizationKeys.admin })
		},
	})
}
